import colors from '@/theme/colors';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Button from '@mui/material/Button';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Grow from '@mui/material/Grow';
import MenuItem from '@mui/material/MenuItem';
import MenuList from '@mui/material/MenuList';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import React, { useState, useRef } from 'react';

import {
  BodyLargeEmphasized,
  BodyLargeMidEmphasis,
  BodySmall,
  TitleMediumEmphasized,
} from '@/design-system/Typography';
import { useLazyQuery } from '@apollo/client';
import { LinkedAccount, LoginStatusResponse } from '@/graphql/types';
import { LOGIN_STATUS } from '@/graphql/queries';
import { ApolloClientName } from '@/config/apolloClient';
import axios from 'axios';
import config from '@/config/config';
import { useSnackbar } from '@/design-system';
import styles from './AccountSwitcher.styles';

const client = axios.create({
  baseURL: config.REACT_APP_ACCOUNTS_BFF_URL,
  withCredentials: true,
});

const getLinkedAccountsSorted = (linkedAccounts: LinkedAccount[]): Array<LinkedAccount> => {
  const sortedBy = ['district', 'school', 'organization', 'family', 'parent'];
  const sortedList = linkedAccounts.sort((a, b) => {
    return sortedBy.indexOf(a.hierarchy.type) - sortedBy.indexOf(b.hierarchy.type);
  });
  return sortedList;
};

interface AccountSwitcherProps {
  linkedAccounts: LinkedAccount[];
  userName: string;
  userEmail?: string;
}

export const AccountSwitcher: React.FC<AccountSwitcherProps> = ({
  linkedAccounts,
  userName,
  userEmail,
}) => {
  const anchorRef = useRef<HTMLButtonElement>(null);
  const showToast = useSnackbar();
  const [open, setOpen] = useState(false);
  const [getLoginStatus] = useLazyQuery<LoginStatusResponse>(LOGIN_STATUS, {
    context: {
      clientName: ApolloClientName.AccountsBff,
    },
    fetchPolicy: 'no-cache',
  });

  const sortedLinkedAccounts = getLinkedAccountsSorted(linkedAccounts);

  const handleToggle = () => {
    setOpen(prevOpen => !prevOpen);
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }

    setOpen(false);
  };

  const getUserType = (scopes: string[]): string => {
    if (scopes.includes('parent')) {
      return 'Parent/Guardian';
    }

    if (scopes.includes('volunteer')) {
      return 'Peachjar Volunteer';
    }

    if (scopes.includes('staff')) {
      return 'Peachjar District';
    }

    if (scopes.includes('org_member')) {
      return 'Peachjar Org';
    }

    return '';
  };

  function handleListKeyDown(event: React.KeyboardEvent) {
    if (event.key === 'Tab') {
      event.preventDefault();
      setOpen(false);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  }

  const handleClickSwitchAccount = async (account: LinkedAccount) => {
    try {
      let destination = `${config.V2_API_HOST_NAME}/dashboard`;
      await client.get(`/auth/sessions/switch-to/${account.userId}`);

      if (['parent', 'family'].includes(account.hierarchy.type)) {
        const { data } = await getLoginStatus();

        if (data) {
          const {
            loginStatus: { redirectTo, loggedIn },
          } = data;
          if (loggedIn) {
            destination = redirectTo ?? '';
          }
        }
      }

      window.location.href = destination;
    } catch (error) {
      console.log(error);
      showToast({ message: 'An error occurred trying to switch accounts.' });
    }
  };
  return (
    <div>
      <div className={styles.buttonWrapper}>
        <Button
          ref={anchorRef}
          className={styles.button}
          id="composition-button"
          aria-controls={open ? 'composition-menu' : undefined}
          aria-expanded={open ? 'true' : undefined}
          onClick={handleToggle}
        >
          <div className={styles.userInfo}>
            <TitleMediumEmphasized nonTranslate className={styles.activeOption} as="h1">
              {userName}
            </TitleMediumEmphasized>
            <ArrowDropDownIcon sx={{ color: colors.prussian, fontSize: '32px' }} />
          </div>
          <BodySmall data-testid="userEmail" style={{ textTransform: 'lowercase' }}>
            {userEmail}
          </BodySmall>
        </Button>
      </div>
      <Popper
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        placement="bottom-start"
        transition
        disablePortal
        className={styles.popper}
      >
        {({ TransitionProps, placement }) => (
          <Grow
            {...TransitionProps}
            style={{
              transformOrigin: placement === 'bottom-start' ? 'left top' : 'left bottom',
              borderRadius: '12px',
              padding: '16px 0px',
            }}
          >
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <>
                  <div className={styles.parentOption}>
                    <BodyLargeEmphasized>Parent/Guardian</BodyLargeEmphasized>
                    <BodySmall color={colors.slate}>{userEmail}</BodySmall>
                  </div>
                  <div className={styles.otherAccountLabel}>
                    <BodySmall color={colors.slate}>Other Accounts</BodySmall>
                  </div>
                  <MenuList
                    autoFocusItem={open}
                    id="composition-menu"
                    aria-labelledby="composition-button"
                    role={undefined}
                    // eslint-disable-next-line react/jsx-no-bind
                    onKeyDown={handleListKeyDown}
                    sx={{ padding: '0px' }}
                  >
                    {sortedLinkedAccounts.map(option => (
                      <MenuItem
                        className={styles.menuItem}
                        onClick={() => handleClickSwitchAccount(option)}
                        key={`${option.hierarchy.type}-${option.email}`}
                      >
                        <div role="button" tabIndex={0} style={{ width: '100%' }}>
                          <div className={styles.optionMenu}>
                            <BodyLargeMidEmphasis>
                              {getUserType(option.scopes)}
                            </BodyLargeMidEmphasis>
                            <BodySmall color={colors.slate}>{option.email}</BodySmall>
                          </div>
                        </div>
                      </MenuItem>
                    ))}
                  </MenuList>
                </>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
    </div>
  );
};
